import app from '@/app';
import { Chart } from 'chart.js';
import templateUrl from './parameterEvolution-resultChartLine.html';

// Scale used to normalize the first aligned multiple, mirroring the
// BigDecimal.setScale(10, HALF_UP) the backend applies in Plan.createBuilders().
const ALIGNMENT_SCALE = 1e10;

/**
 * Builds the milestone list exactly as the backend splits the stretch in
 * Plan.createBuilders(): the first segment is shortened up to the next multiple of the
 * segment length, the following ones use the full step, and the last one stops at the tail.
 *
 * Values are deliberately NOT rounded. The backend accumulates in raw doubles, and its
 * segment filter compares headMilestone by exact equality, so rounding here would produce
 * milestones that match no segment (empty chart) whenever the segment length is fractional.
 */
export function buildAlignedMilestones(headMilestone, tailMilestone, segmentLength) {
	if (headMilestone == null || tailMilestone == null) {
		return [];
	}
	if (segmentLength == null || !(segmentLength > 0)) {
		return [];
	}

	const shifted = headMilestone + segmentLength;
	const remainder = shifted % segmentLength;
	const nextMultiple = Math.round((shifted - remainder) * ALIGNMENT_SCALE) / ALIGNMENT_SCALE;

	let currentLength = remainder !== 0 ? nextMultiple - headMilestone : segmentLength;
	const milestones = [];

	for (let lower = headMilestone; lower < tailMilestone; ) {
		milestones.push(lower);
		const upper = lower + currentLength;
		lower = upper > tailMilestone ? tailMilestone : upper;
		currentLength = segmentLength;
	}

	return milestones;
}

/**
 * Maps the GET .../processingStatus response (issue 011) to the two flags the template
 * and the generate()/fetchData() guard consume.
 */
export function deriveProcessingFlags(status) {
	return {
		isProcessing: status === 'PROCESSING',
		processingFailed: status === 'FAILED'
	};
}

/* @ngInject */
function ParameterEvolutionResultChartLineController($controller, $http, $state, $timeout, $translate, $scope, $rootScope, $element, Principal) {
	var $ctrl = this;
	var chartInstance = null;

	$ctrl.showChart = false;
	$ctrl.tableRows = [];
	$ctrl.rmse = null;
	// Static defaults for now: this controller only reacts to them (guard + template
	// states). Wiring the real backend processing status into these flags is issue 012.
	$ctrl.isProcessing = false;
	$ctrl.processingFailed = false;

	// Metrics that must not be offered in the evolution filter
	const EXCLUDED_METRICS = [
		'hrpro011',
		'hrpro159',
		'hrpro269',
		'cost',
		'area',
		'interventionarea',
		'pavingtype'
	];

	$ctrl.allDirections = ['INC', 'DEC', 'BOTH'];
	$ctrl.allLanes = [];
	$ctrl.metricGroup = m => $translate.instant('unit.' + m.unit);
	$ctrl.filterMetric = m => {
		if (!m || !m.name) return false;
		const name = m.name.toLowerCase();
		if (name.startsWith('before')) return false;
		return EXCLUDED_METRICS.indexOf(name) === -1;
	};

	Principal.identity().then(account => {
		$ctrl.currentAccount = account;
	});

	$ctrl.filterTailYear = headYear => tailYear => tailYear > headYear;

	$controller('AbstractController', { $ctrl: $ctrl });

	var transition = $ctrl.transition;

	$ctrl.transition = params => {
		transition(params);

		$ctrl.direction = params.direction || null;
		$ctrl.lanes = params.lanes || [];
		const metricCode = params.metric || null;
		$ctrl.metric = metricCode && $ctrl.allMetrics
			? ($ctrl.allMetrics.find(m => m.code === metricCode) || null)
			: null;
		$ctrl.headYear = params.headYear || null;
		$ctrl.tailYear = params.tailYear || null;
		// The milestones param has no numeric ui-router type (segments are
		// fractional), so it round-trips through the URL as strings. Coerce back
		// to numbers so the model matches the numeric `allMilestones` choices;
		// otherwise ui-select stops recognizing selected segments after each
		// dynamic param change (e.g. right after generating the chart) and offers
		// them again in the dropdown.
		$ctrl.milestones = (params.milestones || []).map(Number);

		if ($ctrl.entity) {
			var roadsContent = ($ctrl.allRoads && $ctrl.allRoads.content) || $ctrl.allRoads || [];
			var fullRoad = $ctrl.entity.road && roadsContent.find
				? roadsContent.find(r => r.id === $ctrl.entity.road.id)
				: null;
			if (fullRoad && fullRoad.lanesRoad) {
				$ctrl.allLanes = fullRoad.lanesRoad;
			} else if ($ctrl.entity.road && $ctrl.entity.road.lanesRoad) {
				$ctrl.allLanes = $ctrl.entity.road.lanesRoad;
			} else {
				$ctrl.allLanes = [];
			}

			$ctrl.allMilestones = buildAlignedMilestones(
				$ctrl.entity.headMilestone,
				$ctrl.entity.tailMilestone,
				$ctrl.entity.segmentLength
			);

			var campaignsContent = ($ctrl.allCampaigns && $ctrl.allCampaigns.content) || $ctrl.allCampaigns || [];
			var fullCampaign = $ctrl.entity.campaign && campaignsContent.find
				? campaignsContent.find(c => c.id === $ctrl.entity.campaign.id)
				: null;
			var tailDate = (fullCampaign && fullCampaign.tailDate)
				|| ($ctrl.entity.campaign && $ctrl.entity.campaign.tailDate);
			if (tailDate) {
				var baseYear = new Date(tailDate).getFullYear();
				var requestedYears = $ctrl.entity.requestedYears || 0;
				$ctrl.allYears = [];
				for (var y = baseYear; y <= baseYear + requestedYears; y++) {
					$ctrl.allYears.push(y);
				}
			} else {
				$ctrl.allYears = [];
			}

			// Fetches the processing status BEFORE the chart data: fetchData() is only ever
			// triggered from inside its success handler, so the initial load never races
			// ahead of knowing whether the analysis is still processing (issue 011/012).
			fetchProcessingStatus();
		}
	};

	$ctrl.onQueryChange = () => {
		const metricCode = $ctrl.metric && typeof $ctrl.metric === 'object'
			? $ctrl.metric.code
			: $ctrl.metric;
		$state.go(
			$state.current,
			{
				direction: $ctrl.direction,
				lanes: $ctrl.lanes,
				metric: metricCode,
				headYear: $ctrl.headYear,
				tailYear: $ctrl.tailYear,
				milestones: $ctrl.milestones
			},
			{ notify: false, reload: false }
		);
	};

	$ctrl.selectAllMilestones = () => {
		$ctrl.milestones = ($ctrl.allMilestones || []).slice();
	};

	$ctrl.clearMilestones = () => {
		$ctrl.milestones = [];
	};

	$ctrl.generate = () => {
		if (!$ctrl.metric) return;
		if ($ctrl.isProcessing || $ctrl.processingFailed) return;
		$ctrl.onQueryChange();
		fetchData();
	};

	function fetchProcessingStatus() {
		$http.get('api/parameterEvolutions/' + $ctrl.entity.id + '/processingStatus').then(response => {
			const flags = deriveProcessingFlags(response.data);
			$ctrl.isProcessing = flags.isProcessing;
			$ctrl.processingFailed = flags.processingFailed;
			if ($ctrl.metric) {
				fetchData();
			}
		});
	}

	function fetchData() {
		// Guards both entry points (this initial auto-fetch and $ctrl.generate()) against
		// firing while the analysis is in processing or failed state.
		if ($ctrl.isProcessing || $ctrl.processingFailed) return;

		const baseUrl = 'api/parameterEvolutions/' + $ctrl.entity.id;
		const metricObj = $ctrl.metric;
		if (!metricObj) return;

		// Sent as a body, not as query parameters: "select every milestone" on a long road
		// produces hundreds of values, and repeated `milestones=` pairs overflow the server's
		// 8 KB request-line buffer, which answers 400 before routing the request.
		const query = {
			direction: $ctrl.direction,
			lanes: $ctrl.lanes,
			metric: metricObj.code,
			milestones: $ctrl.milestones
		};

		$ctrl.chartError = null;
		$ctrl.noEvolutionData = false;
		$ctrl.isLoading = true;
		$http.post(baseUrl + '/chartData', query).then(response => {
			try {
				buildChart(response.data, metricObj);
			} catch (e) {
				$ctrl.chartError = 'Erro ao montar gráfico: ' + e.message;
				console.error('[parameterEvolution] buildChart error:', e);
			}
		}).catch(err => {
			const status = err && err.status;
			const msg = err && err.data && (err.data.message || err.data.title || JSON.stringify(err.data));
			$ctrl.chartError = 'Erro na requisição' + (status ? ' (' + status + ')' : '') + (msg ? ': ' + msg : '');
			console.error('[parameterEvolution] HTTP error:', err);
		}).finally(() => {
			$ctrl.isLoading = false;
		});
	}

	function buildChart(chartData, metric) {
		const comparisonPoints = (chartData && chartData.comparisonPoints) || [];
		const yearly = ((chartData && chartData.yearlyData) || []).filter(entry =>
			(!$ctrl.headYear || entry.year >= $ctrl.headYear) &&
			(!$ctrl.tailYear || entry.year <= $ctrl.tailYear));

		const hasData = yearly.some(entry => isPlottable(entry.projectedValue));
		if (!hasData) {
			$ctrl.showChart = false;
			$ctrl.noEvolutionData = true;
			return;
		}
		$ctrl.noEvolutionData = false;

		const labels = yearly.map(entry => entry.year);

		// The server already averaged each year across every selected segment, lane and
		// direction; only the metric's display factor is left to apply.
		const projectedByYear = {};
		yearly.forEach(entry => {
			projectedByYear[entry.year] = isPlottable(entry.projectedValue)
				? entry.projectedValue * (metric.factor || 1.0)
				: null;
		});

		const datasets = [{
			label: $translate.instant('parameterEvolution.averageSeries'),
			data: labels
				.filter(year => projectedByYear[year] != null)
				.map(year => ({ x: year, y: projectedByYear[year] })),
			type: 'line',
			borderColor: '#FF8C00',
			backgroundColor: 'rgba(255,140,0,0.1)',
			pointRadius: 2,
			fill: false,
			spanGaps: true
		}];

		// Plotting is limited to the main campaign horizon, just like the table:
		// considered campaigns outside that year range are not shown.
		const headLabel = labels[0];
		const tailLabel = labels[labels.length - 1];
		const visiblePoints = (comparisonPoints || [])
			.filter(p => p.year >= headLabel && p.year <= tailLabel);

		if (visiblePoints.length) {
			const scatterData = visiblePoints
				.map(p => ({ x: p.year, y: p.metricValue, _campaignName: p.campaignName }));
			datasets.push({
				label: $translate.instant('parameterEvolution.consideredCampaigns'),
				data: scatterData,
				type: 'line',
				showLine: false,
				pointStyle: 'crossRot',
				pointRadius: 8,
				borderColor: '#DC3545',
				backgroundColor: '#DC3545',
				pointBorderWidth: 3,
				_comparisonPoints: visiblePoints
			});
		}

		$ctrl.tableRows = labels.map(year => {
			const camps = visiblePoints.filter(p => p.year === year);
			const medio = camps.length
				? camps.reduce((s, p) => s + p.metricValue, 0) / camps.length
				: null;
			const proj = projectedByYear[year];
			return {
				year,
				projectedValue: proj,
				campaigns: camps.map(c => c.campaignName).join(', ') || '—',
				measuredValue: medio,
				error: (proj != null && medio != null) ? (proj - medio) : null
			};
		});
		$ctrl.rmse = chartData && chartData.rmse != null ? chartData.rmse : null;
		$ctrl.showChart = true;

		$timeout(() => renderChart(labels, datasets));
	}

	function renderChart(labels, datasets) {
		const canvas = $element[0].querySelector('#parameterEvolutionChartCanvas');
		if (!canvas) {
			console.error('[parameterEvolution] canvas não encontrado');
			return;
		}
		if (chartInstance) {
			chartInstance.destroy();
			chartInstance = null;
		}
		const wrapper = $element[0].querySelector('#parameterEvolutionChartWrapper');
		if (wrapper) {
			const wrapperWidth = wrapper.clientWidth || 800;
			const avgItemWidth = 180;
			const itemsPerRow = Math.max(1, Math.floor(wrapperWidth / avgItemWidth));
			const legendRows = Math.ceil(datasets.length / itemsPerRow);
			wrapper.style.height = (320 + legendRows * 22) + 'px';
		}
		chartInstance = new Chart(canvas.getContext('2d'), {
			type: 'line',
			data: { datasets: datasets },
			options: {
				responsive: true,
				maintainAspectRatio: false,
				animation: false,
				elements: { line: { fill: false, tension: 0 } },
				scales: {
					xAxes: [{
						type: 'linear',
						position: 'bottom',
						ticks: { stepSize: 1, precision: 0 },
						scaleLabel: { display: true, labelString: $translate.instant('common.year') }
					}],
					yAxes: [{ scaleLabel: { display: true, labelString: $translate.instant('entity.detail.value') } }]
				},
				title: { display: true, text: $translate.instant('parameterEvolution.chart.title') },
				legend: { display: true },
				// Hover/tooltip resolve to the nearest actual point instead of every dataset
				// sharing the same array index. Otherwise a considered-campaign point would be
				// paired with the average-line point at the same index (not the same year),
				// mislabelling its year (e.g. a 2026 campaign shown as 2027).
				hover: { mode: 'nearest', intersect: true },
				tooltips: {
					mode: 'nearest',
					intersect: true,
					callbacks: {
						// The title is the hovered point's own year (its x), so a campaign point
						// reports the same year it has in the table.
						title: function(items, data) {
							if (!items || !items.length) return '';
							const it = items[0];
							const pt = data.datasets[it.datasetIndex].data[it.index];
							return String(pt && pt.x != null ? pt.x : it.xLabel);
						},
						label: function(tooltipItem, data) {
							const ds = data.datasets[tooltipItem.datasetIndex];
							const pt = ds.data[tooltipItem.index];
							if (ds._comparisonPoints && pt && pt._campaignName) {
								return pt._campaignName + ': ' + pt.y.toFixed(3);
							}
							return ds.label + ': ' + tooltipItem.yLabel;
						}
					}
				}
			}
		});
	}

	$scope.$on('$destroy', () => {
		if (chartInstance) {
			chartInstance.destroy();
			chartInstance = null;
		}
	});

	var unregisterTranslateListener = $rootScope.$on('$translateChangeSuccess', () => {
		if ($ctrl.showChart && $ctrl.metric) {
			fetchData();
		}
	});
	$scope.$on('$destroy', () => {
		if (unregisterTranslateListener) unregisterTranslateListener();
	});

	// A year with no measured segment comes back as null and must not reach the canvas.
	function isPlottable(value) {
		return value != null && !isNaN(parseFloat(value)) && isFinite(value);
	}
}

app.component('parameterEvolutionResultChartLine', {
	templateUrl,
	controller: ParameterEvolutionResultChartLineController,
	bindings: {
		$transition$: '<',
		entity: '<',
		allMetrics: '<',
		allRoads: '<',
		allCampaigns: '<'
	}
});
